const AUTH_TOKEN_STORAGE_KEY = 'healo-auth-token';
const AUTH_USER_STORAGE_KEY = 'healo-auth-user';
const REMEMBERED_LOGIN_ID_STORAGE_KEY = 'healo-remembered-login-id';
const AUTH_STATE_CHANGE_EVENT = 'healo-auth-state-change';

let inMemoryToken = '';
let inMemoryUser = null;

function canUseBrowserStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function canUseLocalStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function emitAuthStateChange() {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(AUTH_STATE_CHANGE_EVENT));
}

function readTokenFromStorage() {
  if (!canUseBrowserStorage()) {
    return '';
  }
  return String(window.sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || '').trim();
}

function readUserFromStorage() {
  if (!canUseBrowserStorage()) {
    return null;
  }
  const rawUser = window.sessionStorage.getItem(AUTH_USER_STORAGE_KEY);
  if (!rawUser) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawUser);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
}

// Storage writes are split from event emission so setAuthSession can update both halves of
// the session and emit a SINGLE auth event — subscribers must never observe a transient
// frame where the token belongs to one user and the cached user object to another.
function writeAuthToken(token) {
  inMemoryToken = String(token || '').trim();
  if (!canUseBrowserStorage()) {
    return inMemoryToken;
  }
  if (!inMemoryToken) {
    window.sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    return inMemoryToken;
  }
  window.sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, inMemoryToken);
  return inMemoryToken;
}

function writeAuthUser(user) {
  const normalizedUser = user && typeof user === 'object' ? user : null;
  inMemoryUser = normalizedUser;
  if (!canUseBrowserStorage()) {
    return inMemoryUser;
  }
  if (!inMemoryUser) {
    window.sessionStorage.removeItem(AUTH_USER_STORAGE_KEY);
    return inMemoryUser;
  }
  window.sessionStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(inMemoryUser));
  return inMemoryUser;
}

export function getAuthToken() {
  if (inMemoryToken) {
    return inMemoryToken;
  }
  inMemoryToken = readTokenFromStorage();
  return inMemoryToken;
}

export function setAuthToken(token) {
  const result = writeAuthToken(token);
  emitAuthStateChange();
  return result;
}

export function getAuthUser() {
  if (inMemoryUser) {
    return inMemoryUser;
  }
  inMemoryUser = readUserFromStorage();
  return inMemoryUser;
}

export function setAuthUser(user) {
  const result = writeAuthUser(user);
  emitAuthStateChange();
  return result;
}

/**
 * Atomically replace the whole session (token + user) with ONE auth-state event. This is the
 * only safe way to switch identities: two separate set calls would let subscribers run against
 * a token/user pair from different users.
 */
export function setAuthSession({ token, user } = {}) {
  writeAuthToken(token);
  writeAuthUser(user);
  emitAuthStateChange();
  return { token: inMemoryToken, user: inMemoryUser };
}

export function clearAuthSession() {
  return setAuthSession({ token: '', user: null });
}

export function subscribeAuthStateChange(listener) {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }
  const handler = () => listener();
  window.addEventListener(AUTH_STATE_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handler);
  };
}

export function clearAuthToken() {
  setAuthToken('');
}

export function clearAuthUser() {
  setAuthUser(null);
}

export function getRememberedLoginId() {
  if (!canUseLocalStorage()) {
    return '';
  }
  return String(window.localStorage.getItem(REMEMBERED_LOGIN_ID_STORAGE_KEY) || '').trim();
}

export function setRememberedLoginId(loginId) {
  const normalizedLoginId = String(loginId || '').trim().toLowerCase();
  if (!canUseLocalStorage()) {
    return normalizedLoginId;
  }
  if (!normalizedLoginId) {
    window.localStorage.removeItem(REMEMBERED_LOGIN_ID_STORAGE_KEY);
    return '';
  }
  window.localStorage.setItem(REMEMBERED_LOGIN_ID_STORAGE_KEY, normalizedLoginId);
  return normalizedLoginId;
}
